/**
 * Column access across broker exports.
 *
 * PORTED FROM `tradecoach-saas/app/index.html` — semantics, not code (D3).
 *
 * Every format parser sits on these. Brokers spell the same concept a dozen
 * ways — `FilledQuantity`, `Filled Quantity`, `Fill Qty`, `FillQty` — and the
 * failure mode when a lookup misses is not an error: it is a zero, which
 * becomes a trade with no size that still counts toward every aggregate.
 */

export type Row = Record<string, string>;

/** Normalise a header for fuzzy matching: case, spaces, `_ - / #` all ignored. */
const norm = (k: string): string => k.toLowerCase().replace(/[\s_\-/#]/g, '');

/**
 * First non-empty value among `names`.
 *
 * Two passes, and the order matters: EXACT names first, in the order given, then
 * a normalised match. Exact-first is what lets a caller express priority —
 * `getCol(r, 'FilledQuantity', 'Quantity')` must prefer the actual fill over the
 * *order* quantity, which differs on a partial fill. A single normalised pass
 * would return whichever happened to come first in the row.
 *
 * Empty string is treated as absent, so a blank cell falls through to the next
 * candidate rather than winning.
 */
export function getCol(row: Row, ...names: string[]): string {
  for (const n of names) {
    const v = row[n];
    if (v != null && v !== '') return v;
  }
  const wanted = new Set(names.map(norm));
  for (const k of Object.keys(row)) {
    if (wanted.has(norm(k)) && row[k] !== '') return row[k] as string;
  }
  return '';
}

/**
 * Numeric column. Strips `$ , ( )` and returns 0 when absent or unparseable.
 *
 * ⚠ Note this discards parentheses WITHOUT treating them as negative — use
 * `numColSigned` where sign matters. Both exist in the original and the
 * difference is load-bearing: prices and quantities are magnitudes, P&L is not.
 */
export function numCol(row: Row, ...names: string[]): number {
  const raw = String(getCol(row, ...names) || '').replace(/[$,()]/g, '').trim();
  if (raw === '' || raw === '-') return 0;
  const n = parseFloat(raw);
  return isNaN(n) ? 0 : n;
}

/**
 * Numeric column honouring ACCOUNTING NEGATIVES: `(123.45)` → `-123.45`.
 *
 * Several brokers write losses in parentheses. Reading one with `numCol` yields
 * `+123.45` — a loss recorded as a win, with no error anywhere.
 */
export function numColSigned(row: Row, ...names: string[]): number {
  const raw0 = String(getCol(row, ...names) || '').trim();
  const isAccountingNeg = /^\(.+\)$/.test(raw0);
  const raw = raw0.replace(/[$,()]/g, '').trim();
  if (raw === '' || raw === '-') return 0;
  const n = parseFloat(raw);
  if (isNaN(n)) return 0;
  return isAccountingNeg ? -Math.abs(n) : n;
}

/**
 * Drop duplicate fills by execution id.
 *
 * Sierra's activity log can repeat a fill across exports or overlapping date
 * ranges. Rows with NO id are kept — an unidentifiable fill is more likely a
 * distinct event than a duplicate, and dropping it would silently lose a leg.
 */
export function dedupeFillsById(rows: readonly Row[] | null | undefined): Row[] {
  if (!rows?.length) return (rows ?? []) as Row[];
  const seen = new Set<string>();
  const out: Row[] = [];
  for (const r of rows) {
    const id = String(
      getCol(r, 'FillExecutionServiceID', 'Id', 'TradeId', 'FillId', 'TradeID', 'Order Number', 'orderNumber') || '',
    ).trim();
    if (!id) { out.push(r); continue; }
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(r);
  }
  return out;
}
